import {ChangeDetectionStrategy, Component, Input, OnInit} from '@angular/core';
import {NbThemeService} from '@nebular/theme';
import {SpatialCardVm} from '@theme/components/card-components/spatial-card/spatial-card.vm';
import {BehaviorSubject, combineLatest, Observable} from 'rxjs';
import {FindGeospatialEntitiesResponse, SpatialEntityData} from '../../../../../pages/geospatial-viewer/model/api';
import {filter, map, mergeMap, startWith, takeUntil} from 'rxjs/operators';
import {GeospatialCenterCoordinate} from '../../../../../pages/geospatial-viewer/model/viewer-config';
import {VisualizationType} from '../../../../../pages/geospatial-viewer/model/visualization';
import {
    AssetLayers,
    GenerateLayerService,
    LayerSettings,
} from '@core/utils/engin/geospatial-viewer/esri-layers/generate-layer.service';
import {CoordinateLatLong} from '@core/interfaces/common/asset';
import {FormControl} from '@angular/forms';
import {SpatialCardBaseComponent} from '../spatial-card-base/spatial-card-base.component';
import {GeospatialTooltipType} from '../../../../../pages/geospatial-viewer/model/tooltip.model';
import {
    SpatialCardConfigAsset,
    SpatialCardResponseAsset,
} from '@theme/components/card-components/spatial-card/model/model-spatial-card';

@Component({
    selector: 'ngx-spatial-card-assets',
    templateUrl: './spatial-card-assets.component.html',
    styleUrls: [
        './spatial-card-assets.component.scss',
        '../spatial-card.component.scss',
        '../../../../styles/esri-overrides.scss',
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SpatialCardAssetsComponent extends SpatialCardBaseComponent<SpatialCardConfigAsset> implements OnInit {
    // Override: default map title
    @Input() title: string = 'Geospatial overview: assets';
    // Map data source: map data (i.e., assets), initial location
    @Input() dataSource$: Observable<SpatialCardResponseAsset> = new BehaviorSubject(null);
    // Extra inputs: pan to specific location; settings for map labels
    @Input() panToLocation$: Observable<CoordinateLatLong>;
    @Input() featureSettingsPoint: LayerSettings;
    @Input() featureSettingsLine: LayerSettings;

    // Data store: map, label settings
    private cardResponse: BehaviorSubject<SpatialCardResponseAsset> = new BehaviorSubject(null);
    public toggleFormControl: FormControl = new FormControl({value: true});

    constructor(
        public nbThemeService: NbThemeService,
        public vm: SpatialCardVm,
        public generateLayerService: GenerateLayerService,
    ) {
        super(nbThemeService, vm, generateLayerService);
    }

    ngOnInit(): void {
        super.ngOnInit();
        this.layerIdAlias = 'layer'; // e.g., 'asset_point_layer', 'asset_line_layer', 'group_line_layer'

        this.subscribeMapPanToLocation();
    }

    /*
     * Implement abstract methods
     */
    public subscribeToMapChanges(): void {
        combineLatest([
            this.dataSource$,
            this.nbThemeService.onThemeChange(),
            this.toggleFormControl.valueChanges.pipe(startWith(true)),
        ])
            .pipe(
                takeUntil(this.unsubscribe$),
                map(([resp, themeChange, showStaticLabel]: [SpatialCardResponseAsset, string, boolean]) => {
                    this.cardResponse.next(resp);
                    if (!this.mapInitializedFlag.value) {
                        super.initializeMapSequence(resp.configData);
                    }
                    this.updateAssetLayers(resp.sourceData, themeChange);
                }),
            )
            .subscribe();
    }

    public onSelectMapFeature(firstGraphic: any, _: any[]): void {}

    public onMapViewportChange(newCoordinate: GeospatialCenterCoordinate): void {
        const config: SpatialCardConfigAsset = this.cardResponse.getValue().configData;
        const newConfig: SpatialCardConfigAsset = {
            ...config,
            centerCoordinate: {
                ...config.centerCoordinate,
                ...newCoordinate,
            },
        };

        if (!this.vm.isEquivalent(config.centerCoordinate, newConfig.centerCoordinate)) {
            this.onMapLocationChange.emit(newConfig);
        }
    }

    /*
     * Custom methods
     */
    /** Helper for rendering the map layer(s). Remove existing layers, generate and add new layers from mapDataSource. */
    private updateAssetLayers(mapDataSource: FindGeospatialEntitiesResponse, theme: string): void {
        super.removeMapLayers();

        const mapData: SpatialEntityData[] = mapDataSource.data;
        let visualizationType: VisualizationType = mapDataSource.visualizationType;
        // Render different map layers based on visualization type
        switch (visualizationType) {
            case VisualizationType.INDIVIDUAL:
                const assetLayers: AssetLayers = this.generateLayerService.createAssetFeatureLayers(
                    mapData,
                    visualizationType,
                    theme,
                    this.getPointLayerSettings(),
                    this.getLineLayerSettings(),
                );
                this.map.addMany(assetLayers.layers);
                break;
            case VisualizationType.LINE_CIRCUIT:
            case VisualizationType.LINE_PRID:
            case VisualizationType.LINE_IRID:
            case VisualizationType.LINE_TRANSMISSION:
            case VisualizationType.CUSTOM_GROUP:
                const groupLayers: AssetLayers = this.generateLayerService.createGroupAssetFeatureLayers(
                    mapData,
                    visualizationType,
                    theme,
                    this.getLineLayerSettings(),
                );
                this.map.addMany(groupLayers.layers);
                break;
            default:
                throw new Error('Invalid visualization type selected: ' + visualizationType);
        }
        // Create substation layers regardless of visualization type
        const substationLayer: AssetLayers = this.generateLayerService.createSubstationFeatureLayers(
            mapData,
            visualizationType,
            theme,
            this.getPointLayerSettings(),
        );
        this.map.addMany(substationLayer.layers, 100);
    }

    /** Subscribe to PanToLocation$ observable (input) to update the viewport */
    private subscribeMapPanToLocation(): void {
        this.mapInitializedFlag
            .asObservable()
            .pipe(
                filter((init: boolean) => init),
                mergeMap(() =>
                    this.panToLocation$.pipe(
                        filter((d: CoordinateLatLong) => d != null && d.longitude != null && d.latitude != null),
                    ),
                ),
            )
            .subscribe((init: CoordinateLatLong) => {
                this.mapView.when().then(() => {
                    this.mapView
                        .goTo({
                            center: [init.longitude, init.latitude],
                            zoom: 17.99,
                        })
                        .then(() => {
                            super.updateMapViewport(this.mapView.extent, this.mapView.zoom);
                        });
                });
            });
    }

    /**
     * Gets the asset layer settings object, which determines tooltip, static label, etc.
     * @returns
     */
    private _layerSettingsPoint: LayerSettings = {
        showTooltip: false,
        suppressTooltipTitle: false,
        metrics: [],
        showStaticLabel: true,
        staticLabelProps: {
            expression: `$feature.id`,
            labelPlacement: 'above-center',
            symbol: {
                type: 'text',
                color: 'white',
                backgroundColor: 'black',
                yoffset: 5,
                font: {
                    weight: 'bold',
                    size: '12px',
                },
            },
        },
    };
    // See: https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-support-LabelClass.html#labelPlacement
    private _layerSettingsLine: LayerSettings = {
        showTooltip: false,
        suppressTooltipTitle: false,
        metrics: [],
        showStaticLabel: true,
        staticLabelProps: {
            expression: `$feature.id`,
            labelPlacement: 'above-center',
            symbol: {
                type: 'text',
                color: 'white',
                font: {
                    weight: 'bold',
                    size: '12px',
                },
                // 2D PolyLine FeatureLayer does not support offset and background colour, so use halo instead
                haloSize: '1px',
                haloColor: 'black',
            },
        },
    };
    private getLabelToggleSettings(): {showStaticLabel: boolean; staticLabelProps?: any} {
        if (this.toggleFormControl.value) {
            return {showStaticLabel: true};
        } else {
            return {showStaticLabel: false, staticLabelProps: {}};
        }
    }
    private getPointLayerSettings(): LayerSettings {
        return {
            ...this._layerSettingsPoint,
            ...this.featureSettingsPoint,
            ...this.getLabelToggleSettings(),
        };
    }
    private getLineLayerSettings(): LayerSettings {
        return {
            ...this._layerSettingsLine,
            ...this.featureSettingsLine,
            ...this.getLabelToggleSettings(),
        };
    }
}
